import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import EventBus from '@services/EventBus';
import FormIndex from '@components/workflow/formdialog/FormIndex';

export default function FormDialog({ initialData, customerId }) {
    const { t, i18n } = useTranslation();

    const formIndexRef = useRef(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentElement, setCurrentElement] = useState(null);
    const [currentModeling, setCurrentModeling] = useState(null);
    const [data, setData] = useState('');
    const [isFormValid, setIsFormValid] = useState(false);

    const getNewFormJson = useCallback((bpmnjs) => {
        const elementRegistry = bpmnjs.get('elementRegistry');
        const sendTasks = elementRegistry.filter(element =>
          element.type === 'bpmn:SendTask' && element.businessObject['form-json']
        );
        const newNum = sendTasks.length + 1;
        const newFormJson = {
            name: t('Form {{num}}', { num: newNum }),
            languages: [i18n.language],
            fields: []
        };
        return newFormJson;
    }, [t, i18n]);

    useEffect(() => {
        // Écouter l'événement d'ouverture de la modale
        const handleOpenFormDialog = (data) => {
            setCurrentElement(data.element);
            setCurrentModeling(data.modeling);
            // Récupérer le contenu JSON existant
            if (data.element.businessObject['form-json']) {
                setData(data.element.businessObject['form-json']);
            } else {
                const newFormJson = getNewFormJson(data.bpmnjs);
                setData(JSON.stringify(newFormJson, null, 2));
            }
            setOpenDialog(true);
        };

        EventBus.on('openFormDialog', handleOpenFormDialog);

        // Nettoyage
        return () => {
            EventBus.off('openFormDialog', handleOpenFormDialog);
        };
    }, [initialData, i18n, getNewFormJson]);

    const handleCloseDialog = (event, reason) => {
        if (reason && reason === 'backdropClick') {
            return; // Ne pas fermer si clic sur le fond
        }
        setOpenDialog(false);
        setCurrentElement(null);
        setCurrentModeling(null);
        setData('');
    };

    const handleSaveDialog = () => {

        if (currentElement && formIndexRef.current && currentModeling) {
            const data = formIndexRef.current.getData();
            const jsonData = JSON.stringify(data, null, 2);

            // Émettre un événement pour mettre à jour le JSON
            EventBus.emit('updateFormJson', {
                element: currentElement,
                modeling: currentModeling,
                json: jsonData
            });
        }
        handleCloseDialog();
    };


    return (
        <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {t('Configure form')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent sx={{ pt: 0, minHeight: '360px' }}>
                <FormIndex
                    ref={formIndexRef}
                    customerId={customerId}
                    initialData={data}
                    setIsFormValid={setIsFormValid}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog}>{t('Cancel')}</Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSaveDialog}
                    disabled={!isFormValid}
                >
                    {t('Validate')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}